import { useCallback } from 'react';

/**
 * Custom hook for popup drag and pinch-to-zoom functionality
 * Allows mobile users to drag popups and pinch to resize
 */
const usePopupInteractions = () => {
  const setupPopupInteractions = useCallback((popupElement) => {
    if (!popupElement) return;
    
    const popup = popupElement.closest('.leaflet-popup');
    if (!popup) return;
    
    let isDragging = false;
    let startX = 0, startY = 0;
    let initialLeft = 0, initialTop = 0;
    let scale = 1;
    let initialDistance = 0;
    
    // Get drag handle or use popup header
    const dragHandle = popupElement.querySelector('.popup-drag-handle') || popupElement;
    
    // Drag functionality
    const onDragStart = (e) => {
      if (e.target.closest('button, a, input, select, textarea')) return;
      
      isDragging = true;
      popup.classList.add('popup-draggable');
      
      const touch = e.touches ? e.touches[0] : e;
      startX = touch.clientX;
      startY = touch.clientY;
      
      const rect = popup.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;
      
      e.stopPropagation();
    };
    
    const onDragMove = (e) => {
      if (!isDragging) return;
      
      const touch = e.touches ? e.touches[0] : e;
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      
      popup.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scale})`;
      e.preventDefault();
      e.stopPropagation();
    };
    
    const onDragEnd = () => {
      isDragging = false;
      popup.classList.remove('popup-draggable');
    };
    
    // Pinch-to-zoom functionality
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        initialDistance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        e.preventDefault();
      }
    };
    
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && initialDistance > 0) {
        const currentDistance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        
        const newScale = Math.min(Math.max(scale * (currentDistance / initialDistance), 0.5), 2);
        const contentWrapper = popup.querySelector('.leaflet-popup-content-wrapper');
        if (contentWrapper) {
          contentWrapper.style.transform = `scale(${newScale})`;
        }
        
        initialDistance = currentDistance;
        scale = newScale;
        e.preventDefault();
      }
    };
    
    const onTouchEnd = () => {
      initialDistance = 0;
    };
    
    // Add event listeners
    dragHandle.addEventListener('mousedown', onDragStart);
    dragHandle.addEventListener('touchstart', onDragStart, { passive: false });
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('touchmove', onDragMove, { passive: false });
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('touchend', onDragEnd);
    
    // Pinch zoom listeners on the popup content
    popup.addEventListener('touchstart', onTouchStart, { passive: false });
    popup.addEventListener('touchmove', onTouchMove, { passive: false });
    popup.addEventListener('touchend', onTouchEnd);
    
    // Cleanup function
    return () => {
      dragHandle.removeEventListener('mousedown', onDragStart);
      dragHandle.removeEventListener('touchstart', onDragStart);
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('touchmove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('touchend', onDragEnd);
      popup.removeEventListener('touchstart', onTouchStart);
      popup.removeEventListener('touchmove', onTouchMove);
      popup.removeEventListener('touchend', onTouchEnd);
    };
  }, []);
  
  return { setupPopupInteractions };
};

export default usePopupInteractions;
